import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { useCartStore } from '@/store/cartStore';

import { useTranslation } from 'react-i18next';
import { X, FileText, ChevronDown, ChevronUp, Plus, Printer, Ban, Edit } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { posApi, saasApi } from '@/services/api';
import { isLocalFirstMode, listLocalCustomerInvoices } from '@/offline';
import { useSyncStore } from '@/store/syncStore';
import { useShiftStore } from '@/store/shiftStore';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import ReportDateRangeFilter from '@/components/reports/ReportDateRangeFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import BankAccountSelect from '@/components/common/BankAccountSelect';
import Receipt from '@/components/pos/Receipt';
import AdjustAdvanceModal from './AdjustAdvanceModal';
import { translateInvoiceStatus, translatePaymentMethod } from '@/utils/translateStatus';
import { formatMoney, getCurrencySymbol } from '@/utils/currency';

interface SaleInvoiceLedgerModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: string;
  customerName?: string;
}

export default function SaleInvoiceLedgerModal({
  isOpen,
  onClose,
  customer,
  customerName,
}: SaleInvoiceLedgerModalProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const role = useAuthStore(s => s.role);
  const cart = useCartStore();
  const isAdmin = role === 'Company Admin' || role === 'System Administrator';

  const queryClient = useQueryClient();
  const openShift = useShiftStore((s) => s.openShift);
  const [expandedInvoice, setExpandedInvoice] = useState<string | null>(null);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedInvoiceForPayment, setSelectedInvoiceForPayment] = useState<any>(null);
  const [receiptInvoiceName, setReceiptInvoiceName] = useState<string | null>(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Cash');
  const [bankAccount, setBankAccount] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentDate, setPaymentDate] = useState(new Date().toISOString().slice(0, 10));
  const [attachment, setAttachment] = useState<File | null>(null);
  const [cancellingPayment, setCancellingPayment] = useState<string | null>(null);
  const [adjustModalOpen, setAdjustModalOpen] = useState(false);
  const [selectedInvoiceForAdjust, setSelectedInvoiceForAdjust] = useState<any>(null);
  const [activeTab, setActiveTab] = useState<'Invoices' | 'DirectPayments'>('Invoices');

  const cancelAndEditMutation = useMutation({
    mutationFn: async (inv: any) => {
      let fullInvoice = inv;
      if (!isLocalFirstMode()) {
        const res = await posApi.getInvoiceDetails(inv.name);
        fullInvoice = res.data.message;
      }
      
      await posApi.cancelPOSInvoice(inv.name);
      return fullInvoice;
    },
    onSuccess: (fullInvoice, variables) => {
      cart.clearCart();
      cart.setCustomer(customer, customerName || customer);
      const rawDate = variables.invoice_date || fullInvoice.invoice_date || fullInvoice.posting_date || fullInvoice.creation || '';
      const formattedDate = typeof rawDate === 'string' ? rawDate.split(' ')[0].split('T')[0] : '';
      cart.setEditingInvoiceDate(formattedDate || undefined);
      cart.setNotes(fullInvoice.notes || '');
      cart.setDiscount(Number(fullInvoice.discount_amount) || 0);
      cart.setTax(Number(fullInvoice.tax_amount) || 0);
      
      if (fullInvoice.items) {
        for (const i of fullInvoice.items) {
          cart.addItem({
            item: i.item,
            item_name: i.item_name,
            item_type: i.item_type,
            variant: i.variant,
            variant_name: i.variant_name,
            quantity: i.quantity,
            rate: i.rate,
            amount: i.amount,
          });
        }
      }
      
      onClose();
      navigate('/');
      toast.success('Invoice cancelled. Ready to remake.');
    },
    onError: (err: any) => {
      let msg = 'Failed to cancel invoice';
      if (err.response?.data?._server_messages) {
        try { msg = JSON.parse(err.response.data._server_messages).join('\\n'); } catch(e) {}
      } else if (err.response?.data?.exc_type) {
        msg = `Backend Error: ${err.response.data.exc_type}`;
      } else if (err.message) {
        msg = err.message;
      }
      toast.error(msg);
    }
  });

  const handleEdit = (e: any, inv: any) => {
    e.stopPropagation();
    if (!isAdmin) return toast.error('Only admin can edit invoices');
    
    setConfirmModal({
      open: true,
      title: 'Cancel & Remake Invoice',
      message: `Are you sure you want to cancel and remake invoice ${inv.name}?\n\nThis will permanently cancel the original invoice (and reverse its payments). A new cart will be populated with its items and its previous date automatically.`,
      onConfirm: () => {
        cancelAndEditMutation.mutate(inv);
        setConfirmModal(prev => ({ ...prev, open: false }));
      }
    });
  };

  const [confirmModal, setConfirmModal] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({ open: false, title: '', message: '', onConfirm: () => {} });

  const {
    dateRange, setDateRange,
    customFrom, setCustomFrom,
    customTo, setCustomTo,
    from_date, to_date,
  } = useReportDateRange('This Month');
  const [statusFilter, setStatusFilter] = useState('Unpaid');

  const posSyncMode = useSyncStore((s) => s.posSyncMode);
  const catalogSyncedAt = useSyncStore((s) => s.catalogSyncedAt);

  const { data: customerData } = useQuery({
    queryKey: ['customer', customer],
    queryFn: async () => {
      const res = await posApi.getCustomers();
      return res.data.message.customers.find((c: any) => c.name === customer) || null;
    },
    enabled: isOpen && !!customer,
  });
  
  const availableAdvance = customerData ? Number(customerData.advance_amount) || 0 : 0;

  const { data: invoices, isLoading } = useQuery({
    queryKey: ['customerInvoices', customer, from_date, to_date, statusFilter, posSyncMode, catalogSyncedAt],
    queryFn: async () => {
      if (isLocalFirstMode()) {
        return listLocalCustomerInvoices({
          customer,
          from_date: from_date || undefined,
          to_date: to_date || undefined,
          status: statusFilter || undefined,
        });
      }
      const res = await posApi.getCustomerInvoices({
        customer,
        ...(from_date ? { from_date } : {}),
        ...(to_date ? { to_date } : {}),
        ...(statusFilter ? { status: statusFilter } : {}),
      });
      return res.data.message || [];
    },
    enabled: isOpen && !!customer,
  });

  const { data: directPayments, isLoading: loadingDirectPayments } = useQuery({
    queryKey: ['customerDirectPayments', customer],
    queryFn: async () => {
      const res = await posApi.getCustomerDirectPayments(customer);
      return res.data.message || [];
    },
    enabled: isOpen && !!customer && activeTab === 'DirectPayments',
  });

  const { data: banks = [] } = useQuery({
    queryKey: ['bankAccounts'],
    queryFn: async () => {
      const res = await posApi.getBankAccounts();
      return res.data.message || [];
    },
    enabled: isOpen,
  });

  const addPaymentMutation = useMutation({
    mutationFn: async () => {
      if (!selectedInvoiceForPayment) return;
      if (Number(paymentAmount) > 0 && paymentMethod === 'Bank' && !bankAccount) {
        throw new Error('Select a bank');
      }
      const bank = banks.find((b: { name: string; bank_name: string }) => b.name === bankAccount);
      let fileUrl = '';
      if (attachment) {
        const formData = new FormData();
        formData.append('file', attachment);
        const uploadRes = await saasApi.uploadFile(formData);
        fileUrl = uploadRes.data?.message?.file_url || '';
      }
      await posApi.addSalePayment(selectedInvoiceForPayment.name, {
        amount: Number(paymentAmount),
        attachment: fileUrl,
        payment_method: paymentMethod,
        bank_account: paymentMethod === 'Bank' ? bankAccount : '',
        bank_name: paymentMethod === 'Bank' ? (bank?.bank_name || '') : '',
        reference_no: paymentRef,
        date: paymentDate,
        account: paymentMethod === 'Cash'
          ? (openShift?.pocket_name || '')
          : (bank?.bank_name || ''),
        ...(openShift ? { pos_shift: openShift.name } : {}),
      });
    },
    onSuccess: () => {
      toast.success(t('saleInvoices.paymentAdded'));
      queryClient.invalidateQueries({ queryKey: ['customerInvoices', customer] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setPaymentModalOpen(false);
      setPaymentAmount('');
      setPaymentRef('');
      setPaymentMethod('Cash');
      setBankAccount('');
      setAttachment(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || t('saleInvoices.paymentFailed'));
    },
  });

  const cancelPaymentMutation = useMutation({
    mutationFn: async (paymentName: string) => {
      await posApi.cancelPaymentEntry(paymentName);
    },
    onSuccess: () => {
      toast.success('Payment cancelled successfully');
      queryClient.invalidateQueries({ queryKey: ['customerInvoices', customer] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
      setCancellingPayment(null);
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || 'Failed to cancel payment');
      setCancellingPayment(null);
    },
  });

  const cancelInvoiceMutation = useMutation({
    mutationFn: async (invoiceName: string) => {
      await posApi.cancelPOSInvoice(invoiceName);
    },
    onSuccess: () => {
      toast.success(t('common.cancelled', 'Cancelled successfully'));
      queryClient.invalidateQueries({ queryKey: ['customerInvoices', customer] });
      queryClient.invalidateQueries({ queryKey: ['customers'] });
    },
    onError: (err: any) => {
      toast.error(err.response?.data?.message || err.message || 'Failed to cancel invoice');
    },
  });

  if (!isOpen) return null;

  const statusClass = (status: string) => {
    if (status === 'Paid') return 'bg-emerald-100 text-emerald-700';
    if (status === 'Partially Paid') return 'bg-yellow-100 text-yellow-700';
    if (status === 'Unpaid') return 'bg-red-100 text-red-700';
    if (status === 'Returned' || status === 'Partially Returned') return 'bg-violet-100 text-violet-700';
    if (status === 'Cancelled') return 'bg-slate-200 text-slate-600';
    return 'bg-gray-100 text-gray-700';
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-end sm:items-center justify-center z-50 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-5xl max-h-[95vh] sm:max-h-[90vh] flex flex-col overflow-hidden">
        <div className="flex items-center justify-between p-4 sm:p-5 border-b border-gray-100 shrink-0">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-10 h-10 bg-primary-50 text-primary-600 rounded-lg flex items-center justify-center shrink-0">
              <FileText className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h3 className="font-semibold text-gray-900 text-base sm:text-lg">{t('saleInvoices.title')}</h3>
              <p className="text-sm text-gray-500 truncate">{customerName || customer}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors shrink-0">
            <X className="w-5 h-5 text-gray-400" />
          </button>
        </div>

        <div className="flex border-b border-gray-100 bg-white px-4 pt-2 shrink-0">
          <button
            type="button"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'Invoices' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('Invoices')}
          >
            {t('saleInvoices.title', 'Invoices')}
          </button>
          <button
            type="button"
            className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${activeTab === 'DirectPayments' ? 'border-primary-600 text-primary-600' : 'border-transparent text-gray-500 hover:text-gray-700'}`}
            onClick={() => setActiveTab('DirectPayments')}
          >
            Direct Payments
          </button>
        </div>

        {activeTab === 'Invoices' ? (
          <>
            <div className="px-4 pt-4 pb-2 border-b border-gray-100 bg-gray-50/50 shrink-0">
              <div className="flex flex-col sm:flex-row sm:items-end gap-3">
            <ReportDateRangeFilter
              dateRange={dateRange}
              onDateRangeChange={setDateRange}
              customFrom={customFrom}
              customTo={customTo}
              onCustomFromChange={setCustomFrom}
              onCustomToChange={setCustomTo}
              className="flex-1"
            />
            <div className="w-full sm:w-48">
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('saleInvoices.status')}</label>
              <select
                className="input-field py-1.5 text-sm"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option value="Unpaid">{translateInvoiceStatus('Unpaid', t)}</option>
                <option value="Paid">{translateInvoiceStatus('Paid', t)}</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 sm:p-5">
          {isLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : invoices?.length === 0 ? (
            <div className="text-center py-12 text-gray-500">{t('saleInvoices.empty')}</div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
              {/* Header row (desktop) */}
              <div className="hidden sm:grid grid-cols-[minmax(0,1.4fr)_0.9fr_0.7fr_0.9fr_0.9fr_auto] gap-3 px-3 py-2 bg-gray-50 border-b border-gray-100 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                <div>{t('saleInvoices.invoice')}</div>
                <div>{t('common.date')}</div>
                <div>{t('common.status')}</div>
                <div className="text-right">{t('saleInvoices.netTotal')}</div>
                <div className="text-right">{t('saleInvoices.balance')}</div>
                <div className="min-w-[100px]" />
              </div>

              <div className="divide-y divide-gray-100">
                {invoices?.map((inv: any) => {
                  const balanceDue = Math.max(0, Number(inv.balance_amount) || 0);
                  const terminalReturn =
                    inv.status === 'Returned' || inv.status === 'Partially Returned' || inv.status === 'Cancelled';
                  // Never treat returned/voided as Paid just because balance is 0
                  const isPaid = !terminalReturn && (inv.status === 'Paid' || balanceDue <= 0);
                  const isExpanded = expandedInvoice === inv.name;

                  return (
                    <div
                      key={inv.name}
                      className={isExpanded ? 'bg-gray-50/40' : 'bg-white'}
                    >
                      <div
                        className="px-3 py-2.5 cursor-pointer hover:bg-gray-50 transition-colors"
                        onClick={() => setExpandedInvoice(isExpanded ? null : inv.name)}
                      >
                        {/* Desktop */}
                        <div className="hidden sm:grid grid-cols-[minmax(0,1.4fr)_0.9fr_0.7fr_0.9fr_0.9fr_auto] gap-3 items-center">
                          <div className="min-w-0 flex items-center gap-2">
                            <span className="font-medium text-gray-900 text-sm truncate">{inv.name}</span>
                            {inv.customer_birth_year ? (
                              <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 text-amber-700 border border-amber-200">
                                YOB: {inv.customer_birth_year}
                              </span>
                            ) : null}
                          </div>

                          <div className="text-sm text-gray-600">
                            {inv.invoice_date ? format(new Date(inv.invoice_date), 'dd MMM yyyy') : '-'}
                          </div>

                          <div>
                            <span className={`inline-block px-1.5 py-0.5 text-[10px] font-medium rounded ${statusClass(inv.status)}`}>
                              {translateInvoiceStatus(inv.status, t)}
                            </span>
                          </div>

                          <div className="text-sm font-medium text-gray-900 text-right">
                            {formatMoney(Number(inv.net_total || 0))}
                          </div>

                          <div
                            className={`text-sm font-medium text-right ${balanceDue > 0 ? 'text-amber-600' : 'text-emerald-600'}`}
                          >
                            {formatMoney(balanceDue)}
                          </div>

                          <div className="flex items-center justify-end gap-1.5 shrink-0 flex-nowrap min-w-[100px]">
                            {isAdmin && (
                              <button
                                type="button"
                                title="Edit / Remake"
                                onClick={(e) => handleEdit(e, inv)}
                                className="inline-flex items-center justify-center p-1.5 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                            )}
                            {!isPaid && !isLocalFirstMode() && (
                              <>

                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedInvoiceForPayment(inv);
                                    setPaymentAmount(String(balanceDue));
                                    setPaymentModalOpen(true);
                                  }}
                                  className="shrink-0 px-2 py-1 text-[11px] bg-primary-50 text-primary-600 hover:bg-primary-100 font-medium rounded transition-colors inline-flex items-center gap-0.5 whitespace-nowrap"
                                >
                                  <Plus className="w-3 h-3" /> {t('common.pay')}
                                </button>
                              </>
                            )}
                            <button
                              type="button"
                              title={t('saleInvoices.reprint')}
                              onClick={(e) => {
                                e.stopPropagation();
                                setReceiptInvoiceName(inv.name);
                              }}
                              className="inline-flex items-center justify-center p-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                            >
                              <Printer className="w-4 h-4" />
                            </button>
                            {inv.status !== 'Cancelled' && (
                              <button
                                type="button"
                                title={t('common.cancel', 'Cancel Invoice')}
                                disabled={cancelInvoiceMutation.isPending}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmModal({
                                    open: true,
                                    title: t('common.cancel', 'Cancel Invoice'),
                                    message: 'Are you sure you want to cancel this invoice?',
                                    onConfirm: () => {
                                      cancelInvoiceMutation.mutate(inv.name);
                                      setConfirmModal(prev => ({ ...prev, open: false }));
                                    }
                                  });
                                }}
                                className="inline-flex items-center justify-center p-1.5 rounded bg-red-50 text-red-700 hover:bg-red-100 transition-colors disabled:opacity-50"
                              >
                                <Ban className="w-4 h-4" />
                              </button>
                            )}
                            <div className="text-gray-400">
                              {isExpanded ? (
                                <ChevronUp className="w-4 h-4" />
                              ) : (
                                <ChevronDown className="w-4 h-4" />
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Mobile */}
                        <div className="sm:hidden">
                          <div className="grid grid-cols-[1fr_auto] items-start gap-2">
                            <div className="min-w-0">
                              <div className="flex items-center gap-2 min-w-0">
                                <span className="font-medium text-gray-900 text-sm truncate">{inv.name}</span>
                                {inv.customer_birth_year ? (
                                  <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-50 text-amber-700 border border-amber-200">
                                    YOB: {inv.customer_birth_year}
                                  </span>
                                ) : null}
                                <span className={`shrink-0 px-1.5 py-0.5 text-[10px] font-medium rounded ${statusClass(inv.status)}`}>
                                  {translateInvoiceStatus(inv.status, t)}
                                </span>
                              </div>
                            </div>
                            <div className="flex items-center gap-1.5 shrink-0 flex-nowrap">
                              {isAdmin && (
                                <button
                                  type="button"
                                  title="Edit / Remake"
                                  onClick={(e) => handleEdit(e, inv)}
                                  className="inline-flex items-center justify-center p-1.5 rounded bg-amber-50 text-amber-700 hover:bg-amber-100 transition-colors"
                                >
                                  <Edit className="w-4 h-4" />
                                </button>
                              )}
                              {!isPaid && !isLocalFirstMode() && (
                                <>
                                  {availableAdvance > 0 && (
                                    <button
                                      type="button"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setSelectedInvoiceForAdjust(inv);
                                        setAdjustModalOpen(true);
                                      }}
                                      className="shrink-0 px-2 py-1 text-[11px] bg-amber-50 text-amber-600 hover:bg-amber-100 font-medium rounded transition-colors inline-flex items-center gap-0.5 whitespace-nowrap"
                                    >
                                      Adjust Advance
                                    </button>
                                  )}
                                  <button
                                    type="button"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setSelectedInvoiceForPayment(inv);
                                      setPaymentAmount(String(balanceDue));
                                      setPaymentModalOpen(true);
                                    }}
                                    className="shrink-0 px-2 py-1 text-[11px] bg-primary-50 text-primary-600 hover:bg-primary-100 font-medium rounded transition-colors inline-flex items-center gap-0.5 whitespace-nowrap"
                                  >
                                    <Plus className="w-3 h-3" /> {t('common.pay')}
                                  </button>
                                </>
                              )}
                              <button
                                type="button"
                                title={t('saleInvoices.reprint')}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setReceiptInvoiceName(inv.name);
                                }}
                                className="inline-flex items-center justify-center p-1.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                              >
                                <Printer className="w-4 h-4" />
                              </button>
                              <div className="text-gray-400">
                                {isExpanded ? (
                                  <ChevronUp className="w-4 h-4" />
                                ) : (
                                  <ChevronDown className="w-4 h-4" />
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="mt-1 grid grid-cols-3 gap-2 text-xs">
                            <div>
                              <div className="text-gray-400">{t('common.date')}</div>
                              <div className="text-gray-700">
                                {inv.invoice_date ? format(new Date(inv.invoice_date), 'dd MMM yyyy') : '-'}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-gray-400">Net</div>
                              <div className="font-medium text-gray-900">
                                {formatMoney(Number(inv.net_total || 0))}
                              </div>
                            </div>
                            <div className="text-right">
                              <div className="text-gray-400">Bal</div>
                              <div
                                className={`font-medium ${balanceDue > 0 ? 'text-amber-600' : 'text-emerald-600'}`}
                              >
                                {formatMoney(balanceDue)}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Expanded payments */}
                      {isExpanded && (
                        <div className="border-t border-gray-100 p-3 sm:p-4 bg-gray-50/50 space-y-4">
                          {Number(inv.write_off_amount) > 0 && (
                            <p className="text-sm text-gray-600">
                              Write off: {formatMoney(Number(inv.write_off_amount))}
                            </p>
                          )}
                          <div>
                            <h4 className="text-sm font-medium text-gray-700 mb-2">Payments</h4>
                            {inv.payments?.length > 0 ? (
                              <>
                                <div className="hidden sm:block">
                                  <table className="w-full text-sm">
                                    <thead>
                                      <tr className="text-left text-xs text-gray-500 uppercase">
                                        <th className="pb-2">Date</th>
                                        <th className="pb-2">Method</th>
                                        <th className="pb-2">Reference</th>
                                        <th className="pb-2 text-right">Amount</th>
                                        <th className="pb-2 text-right">Actions</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {inv.payments.map((payment: any, idx: number) => (
                                        <tr key={idx} className="border-t border-gray-100">
                                          <td className="py-2 text-gray-600">
                                            {payment.date ? format(new Date(payment.date), 'dd MMM yyyy') : '-'}
                                          </td>
                                          <td className="py-2 text-gray-600">
                                            {payment.payment_method}
                                            {payment.bank_name ? ` (${payment.bank_name})` : payment.account && payment.payment_method === 'Bank' ? ` (${payment.account})` : ''}
                                          </td>
                                          <td className="py-2 text-gray-600">{payment.reference_no || '-'}</td>
                                          <td className="py-2 text-right font-medium text-emerald-600">
                                            {formatMoney(Number(payment.amount))}
                                          </td>
                                          <td className="py-2 text-right">
                                            {payment.docstatus === 2 ? (
                                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                                {t('common.cancelled', 'Cancelled')}
                                              </span>
                                            ) : (
                                              <button
                                                type="button"
                                                className="text-red-600 hover:text-red-800 disabled:opacity-50"
                                                disabled={cancelPaymentMutation.isPending && cancellingPayment === payment.name}
                                                onClick={() => {
                                                  setCancellingPayment(payment.name);
                                                  cancelPaymentMutation.mutate(payment.name);
                                                }}
                                                title="Cancel payment"
                                              >
                                                <Ban size={16} />
                                              </button>
                                            )}
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                                <div className="sm:hidden space-y-2">
                                  {inv.payments.map((payment: any, idx: number) => (
                                    <div key={idx} className="bg-white border border-gray-200 rounded-lg p-3">
                                      <div className="flex justify-between items-start gap-2">
                                        <div>
                                          <div className="text-sm font-medium text-gray-900">
                                            {payment.payment_method}
                                            {payment.bank_name ? ` (${payment.bank_name})` : payment.account && payment.payment_method === 'Bank' ? ` (${payment.account})` : ''}
                                          </div>
                                          <div className="text-xs text-gray-500">
                                            {payment.date ? format(new Date(payment.date), 'dd MMM yyyy') : '-'}
                                            {payment.reference_no ? ` · ${payment.reference_no}` : ''}
                                          </div>
                                        </div>
                                        <div className="text-sm font-medium text-emerald-600 whitespace-nowrap">
                                          {formatMoney(Number(payment.amount))}
                                          <button
                                            type="button"
                                            className="ml-2 text-red-600 hover:text-red-800 disabled:opacity-50"
                                            disabled={cancelPaymentMutation.isPending && cancellingPayment === payment.name}
                                            onClick={() => {
                                              setCancellingPayment(payment.name);
                                              cancelPaymentMutation.mutate(payment.name);
                                            }}
                                            title="Cancel payment"
                                          >
                                            <Ban size={16} />
                                          </button>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </>
                            ) : (
                              <p className="text-sm text-gray-400">No payments recorded yet.</p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
          </>
        ) : (
          <div className="flex-1 overflow-y-auto p-4 sm:p-5 bg-gray-50">
            {loadingDirectPayments ? (
              <div className="flex items-center justify-center h-40">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
              </div>
            ) : directPayments?.length === 0 ? (
              <div className="text-center py-12 text-gray-500">No direct payments found.</div>
            ) : (
              <div className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                <div className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 px-3 py-2 bg-gray-50 border-b border-gray-100 text-[11px] font-medium text-gray-500 uppercase tracking-wider">
                  <div>Date</div>
                  <div>Payment Method</div>
                  <div>Reference</div>
                  <div className="text-right">Amount</div>
                  <div className="w-8"></div>
                </div>
                <div className="divide-y divide-gray-100">
                  {directPayments?.map((payment: any) => (
                    <div key={payment.name} className="grid grid-cols-[1fr_1fr_1fr_1fr_auto] gap-3 px-3 py-3 text-sm items-center hover:bg-gray-50">
                      <div className="text-gray-900">{payment.posting_date ? format(new Date(payment.posting_date), 'dd MMM yyyy') : '-'}</div>
                      <div className="text-gray-900">{payment.payment_method} {payment.bank_name ? `(${payment.bank_name})` : ''}</div>
                      <div className="text-gray-600">{payment.reference_no || '-'}</div>
                      <div className="text-right font-medium text-emerald-600">{formatMoney(Number(payment.amount))}</div>
                      <div>
                        {payment.docstatus === 2 ? (
                          <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                            {t('common.cancelled', 'Cancelled')}
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="text-red-500 hover:text-red-700 disabled:opacity-50 p-1.5"
                            disabled={cancelPaymentMutation.isPending && cancellingPayment === payment.name}
                            onClick={() => {
                              setConfirmModal({
                                open: true,
                                title: 'Cancel Payment',
                                message: 'Are you sure you want to cancel this payment?',
                                onConfirm: () => {
                                  setCancellingPayment(payment.name);
                                  cancelPaymentMutation.mutate(payment.name);
                                  setConfirmModal(prev => ({ ...prev, open: false }));
                                }
                              });
                            }}
                          >
                            <Ban className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {receiptInvoiceName && (
        <Receipt
          invoiceName={receiptInvoiceName}
          onClose={() => setReceiptInvoiceName(null)}
        />
      )}

      {paymentModalOpen && selectedInvoiceForPayment && (
        <div className="fixed inset-0 bg-black/60 flex items-end sm:items-center justify-center z-[60] p-0 sm:p-4">
          <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-sm max-h-[95vh] overflow-hidden flex flex-col">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50 shrink-0">
              <h4 className="font-semibold text-gray-900">{t('saleInvoices.addPayment')}</h4>
              <button type="button" onClick={() => { setPaymentModalOpen(false); setAttachment(null); }} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-4 sm:p-5 space-y-4 overflow-y-auto flex-1">
              {availableAdvance > 0 && (
                <div className="bg-amber-50 p-3 rounded-lg border border-amber-200 flex items-center justify-between">
                  <div>
                    <p className="text-sm text-amber-900 font-medium">Available Advance: {formatMoney(availableAdvance)}</p>
                    <p className="text-xs text-amber-700 mt-0.5">Settle invoice using existing advance.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setPaymentModalOpen(false);
                      setSelectedInvoiceForAdjust(selectedInvoiceForPayment);
                      setAdjustModalOpen(true);
                    }}
                    className="shrink-0 ml-3 px-3 py-1.5 text-xs bg-amber-600 text-white rounded hover:bg-amber-700 font-medium shadow-sm"
                  >
                    Adjust Advance
                  </button>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.date')}</label>
                <input
                  type="date"
                  value={paymentDate}
                  onChange={(e) => setPaymentDate(e.target.value)}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('reports.paymentMethod')}</label>
                <select
                  value={paymentMethod}
                  onChange={(e) => {
                    const next = e.target.value;
                    setPaymentMethod(next);
                    if (next === 'Bank' && !bankAccount && banks.length) {
                      const def = banks.find((b: { is_default: number }) => b.is_default) || banks[0];
                      setBankAccount(def.name);
                    }
                    if (next !== 'Bank') setBankAccount('');
                  }}
                  className="input-field w-full"
                >
                  <option value="Cash">{translatePaymentMethod('Cash', t)}</option>
                  <option value="Bank">{translatePaymentMethod('Bank', t)}</option>
                </select>
              </div>
              {paymentMethod === 'Bank' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Bank *</label>
                  <BankAccountSelect
                    value={bankAccount}
                    onChange={setBankAccount}
                    banks={banks}
                    required
                  />
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('common.amount')} ({getCurrencySymbol()})</label>
                <input
                  type="number"
                  min={0}
                  step="0.01"
                  value={paymentAmount}
                  onChange={(e) => setPaymentAmount(e.target.value)}
                  max={selectedInvoiceForPayment.balance_amount}
                  className="input-field w-full"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Balance due: {formatMoney(Number(selectedInvoiceForPayment.balance_amount))}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('supplierPayment.referenceOptional')}</label>
                <input
                  type="text"
                  value={paymentRef}
                  onChange={(e) => setPaymentRef(e.target.value)}
                  className="input-field w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Attachment</label>
                <input
                  type="file"
                  onChange={(e) => setAttachment(e.target.files?.[0] || null)}
                  className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                />
              </div>
            </div>
            <div className="p-4 sm:p-5 border-t border-gray-100 bg-gray-50 flex gap-3 shrink-0">
              <button
                type="button"
                onClick={() => { setPaymentModalOpen(false); setAttachment(null); }}
                className="w-full sm:w-auto px-4 py-2.5 sm:py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg"
              >
                {t('common.cancel')}
              </button>
              <button
                type="button"
                disabled={addPaymentMutation.isPending || !paymentAmount || Number(paymentAmount) <= 0}
                onClick={() => addPaymentMutation.mutate()}
                className="w-full sm:flex-1 btn-primary py-2.5 disabled:opacity-50"
              >
                {addPaymentMutation.isPending ? t('common.saving') : t('supplierPayment.savePayment')}
              </button>
            </div>
          </div>
        </div>
      )}
      {adjustModalOpen && selectedInvoiceForAdjust && (
        <AdjustAdvanceModal
          isOpen={adjustModalOpen}
          onClose={() => setAdjustModalOpen(false)}
          customer={customer}
          invoice={selectedInvoiceForAdjust}
          availableAdvance={availableAdvance}
        />
      )}

      {confirmModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[70] p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm flex flex-col overflow-hidden">
            <div className="p-4 border-b border-gray-100 flex justify-between items-center bg-gray-50">
              <h4 className="font-semibold text-gray-900">{confirmModal.title}</h4>
              <button
                type="button"
                onClick={() => setConfirmModal((prev) => ({ ...prev, open: false }))}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-gray-700">{confirmModal.message}</p>
            </div>
            <div className="p-4 border-t border-gray-100 flex justify-end gap-2 bg-gray-50">
              <button
                type="button"
                onClick={() => setConfirmModal((prev) => ({ ...prev, open: false }))}
                className="btn-secondary"
              >
                {t('common.cancel', 'No')}
              </button>
              <button
                type="button"
                onClick={confirmModal.onConfirm}
                className="btn-primary bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700"
              >
                {t('common.confirm', 'Yes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
